import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, RefreshCw } from "lucide-react";
import { Button, cn } from "@nexus/ui";
import { useServerStatus } from "@/hooks/useServerStatus";
import { resetConnectivityState } from "@/hooks/connectivityTracker";
import { serverStatusKeys } from "@/lib/queries";

/** How long the "Back online" pill stays visible after recovery. */
export const BACK_ONLINE_BANNER_MS = 2_500;

/** Pill enter animation duration (ms) — keep in sync with Tailwind `duration-200`. */
export const CONNECTION_BANNER_ENTER_MS = 200;

/** Pill exit animation duration (ms) — keep in sync with Tailwind `duration-150`. */
export const CONNECTION_BANNER_EXIT_MS = 150;

type ConnectionBannerTone = "offline" | "backOnline";

export interface ConnectionStatusPillViewProps {
  tone: ConnectionBannerTone;
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
  isRetrying?: boolean;
  isExiting?: boolean;
}

/** @deprecated Use ConnectionStatusPillView — kept for Storybook/test imports during rename. */
export type ConnectionStatusBannerViewProps = ConnectionStatusPillViewProps;

/**
 * Floating connection-status pill (WhatsApp / Linear pattern).
 * Persistent while offline; morphs to success briefly on recovery.
 * Prefer this over full-width pastel banners — less layout noise, clearer status.
 */
export function ConnectionStatusPillView({
  tone,
  message,
  onRetry,
  retryLabel,
  isRetrying = false,
  isExiting = false,
}: ConnectionStatusPillViewProps) {
  const isOffline = tone === "offline";

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={cn(
        "pointer-events-auto fixed z-(--z-connection-status) flex max-w-[calc(100vw-1.5rem)] items-center gap-2",
        "rounded-full border px-3 py-2 shadow-lg sm:gap-2.5 sm:px-4",
        // Mobile: bottom-center (thumb + avoids sticky filter chrome). Desktop: top-center.
        "bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2",
        "sm:bottom-auto sm:top-[max(0.75rem,env(safe-area-inset-top))]",
        isOffline
          ? "border-transparent bg-[var(--foreground)] text-[var(--background)]"
          : "border-transparent bg-[var(--success)] text-[var(--success-foreground)]",
        isExiting
          ? "motion-safe:animate-out motion-safe:fade-out motion-safe:zoom-out-95 motion-safe:duration-150 motion-safe:ease-in motion-reduce:animate-none"
          : "motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-200 motion-safe:ease-out motion-reduce:animate-none",
      )}
    >
      {isOffline ? (
        <span className="relative flex size-2 shrink-0" aria-hidden>
          <span className="absolute inset-0 rounded-full bg-[var(--warning)] opacity-70 motion-safe:animate-ping motion-reduce:animate-none" />
          <span className="relative size-2 rounded-full bg-[var(--warning)]" />
        </span>
      ) : (
        <Check className="size-3.5 shrink-0" strokeWidth={2.5} aria-hidden />
      )}

      <p className="min-w-0 truncate text-xs font-medium tracking-tight sm:text-sm">
        {message}
      </p>

      {isOffline && onRetry && retryLabel ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRetry}
          disabled={isRetrying}
          className={cn(
            "h-8 shrink-0 cursor-pointer gap-1.5 rounded-full px-2.5 text-xs",
            "bg-[var(--background)]/12 text-[var(--background)]",
            "hover:bg-[var(--background)]/20 hover:text-[var(--background)]",
            "focus-visible:ring-[var(--background)]/40",
            "disabled:opacity-50",
          )}
        >
          {isRetrying ? (
            <Loader2
              className="size-3.5 motion-safe:animate-spin motion-reduce:animate-none"
              aria-hidden
            />
          ) : (
            <RefreshCw className="size-3.5" aria-hidden />
          )}
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}

/** Alias so existing stories/tests keep compiling during the visual redesign. */
export const ConnectionStatusBannerView = ConnectionStatusPillView;

type BannerPhase = "hidden" | "offline" | "backOnline";

/**
 * Owns the /healthz heartbeat, connection status pill, and recovery refresh.
 * Mount only inside the authenticated layout so polling does not run on public routes.
 */
export function ConnectionLostBanner() {
  const { t } = useTranslation();
  const serverStatus = useServerStatus();
  const wasOffline = useRef(false);
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<BannerPhase>("hidden");
  const [isExiting, setIsExiting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (serverStatus === "offline") {
      wasOffline.current = true;
      clearTimers();
      setIsExiting(false);
      setPhase("offline");
      setIsRetrying(false);
      return;
    }

    if (!wasOffline.current) return;

    wasOffline.current = false;
    clearTimers();
    setIsExiting(false);
    setPhase("backOnline");
    setIsRetrying(false);
    void queryClient.invalidateQueries();

    dismissTimerRef.current = setTimeout(() => {
      setIsExiting(true);
      exitTimerRef.current = setTimeout(() => {
        setPhase("hidden");
        setIsExiting(false);
        exitTimerRef.current = null;
      }, CONNECTION_BANNER_EXIT_MS);
      dismissTimerRef.current = null;
    }, BACK_ONLINE_BANNER_MS);
  }, [serverStatus, queryClient]);

  useEffect(() => {
    return () => {
      clearTimers();
      resetConnectivityState();
    };
  }, []);

  const handleRetry = () => {
    setIsRetrying(true);
    void queryClient
      .refetchQueries({ queryKey: serverStatusKeys.all })
      .finally(() => {
        setIsRetrying(false);
      });
  };

  if (phase === "hidden") return null;

  const message =
    phase === "offline"
      ? t("common.serverStatus.connectionLost", "Reconnecting…")
      : t("common.serverStatus.backOnline", "Connected");

  return (
    <ConnectionStatusPillView
      tone={phase}
      message={message}
      isExiting={isExiting}
      onRetry={phase === "offline" ? handleRetry : undefined}
      retryLabel={
        phase === "offline" ? t("common.serverStatus.retryNow", "Retry") : undefined
      }
      isRetrying={isRetrying}
    />
  );
}
